import { useCallback, useEffect, useState } from 'react';
import { Bell, TicketPercent, CalendarClock } from 'lucide-react';
import { supabase } from '../services/supabase';
import { leerVarias, listaEnCastellano } from '../lib/leerVarias';
import { alumnosDistintos } from '../lib/suscritos';

// Sección 3 del Dashboard (PR-UX2): avisos SIN acción requerida. Solo informan
// de cosas que están a punto de caducar en los próximos 7 días.
//   - Promociones (discount_codes) activas con valid_until dentro de 7 días.
//   - Alumnos con alguna suscripción activada cuyo término acaba dentro de 7 días.
//
// EL AVISO DE SUSCRIPCIONES DERIVABA LA FECHA DEL MES, Y DEJÓ DE SER VERDAD.
//   Contaba las activadas cuyo `period_month` terminaba en los próximos 7 días,
//   calculando el fin de mes a mano. Con la mig 315 una suscripción cubre
//   `term_months` meses: los siete trimestres del pack tienen `period_month`
//   octubre y acaban el 31 de diciembre, así que este panel habría avisado a
//   finales de octubre de que caducaban siete suscripciones que siguen vivas dos
//   meses más. Un aviso falso en el panel es peor que ninguno.
//
//   Ahora se lee `expires_at`, que es la fecha real de fin y la única
//   autoridad: la mig 316 la sella al activar y su CHECK garantiza que una
//   'activated' nunca la tiene a NULL. Eso permite además filtrar en la
//   consulta (como las promos) en vez de en el cliente: se va la aritmética de
//   calendario entera.
//
// Y CONTABA FILAS, NO ALUMNOS. Que es otra cosa: `user_subscriptions` es una
//   tabla de compras y conviven dos formas de guardar el mismo trimestre. Hoy
//   mismo hay un alumno con TRES filas activadas a la vez —el modelo viejo,
//   `bundle_seq` 1 y 2— que caducan el 31-oct, el 30-nov y el 31-dic: la semana
//   del 31 de octubre este aviso diría «1» y sería el único de los tres que
//   importa, pero si los tres cayeran en la misma ventana diría «3 suscripciones
//   expiran» por una sola persona a la que avisar. El aviso existe para llamar a
//   alguien, así que cuenta gente: `count(DISTINCT user_id)`.
//
// El `try/catch` que envolvía las dos lecturas era código muerto: postgrest-js
// no rechaza nunca (devuelve `{ data: null, error }` mientras
// `shouldThrowOnError` sea false), así que `setError(true)` no se ejecutaba
// jamás y un 403 de RLS llegaba aquí como lista vacía. En un panel de avisos
// eso no es un hueco: es la frase "no caduca nada esta semana", que es
// exactamente la que hace que nadie vuelva a mirar. Ahora cada cifra puede
// estar SIN COMPROBAR y se pinta como tal.

const QUE_PROMOS = 'las promociones';
const QUE_SUBS = 'las suscripciones';

/** null = no se pudo leer. No es 0: un 0 afirma que no caduca nada. */
type Cuenta = number | null;

interface ClubAvisos {
  promosExpiring: Cuenta;
  subsExpiring: Cuenta;
}

const EMPTY: ClubAvisos = { promosExpiring: 0, subsExpiring: 0 };

export default function AvisosClubPanel() {
  const [avisos, setAvisos] = useState<ClubAvisos>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [fallidos, setFallidos] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const now = Date.now();
    const in7d = now + 7 * 24 * 60 * 60 * 1000;
    const nowIso = new Date(now).toISOString();
    const in7dIso = new Date(in7d).toISOString();

    const { datos, fallidos: noLeidos } = await leerVarias([
      {
        que: QUE_PROMOS,
        // Promos activas que caducan en los próximos 7 días.
        consulta: supabase
          .from('discount_codes')
          .select('id')
          .eq('active', true)
          .not('valid_until', 'is', null)
          .gte('valid_until', nowIso)
          .lte('valid_until', in7dIso),
      },
      {
        que: QUE_SUBS,
        // Suscripciones activadas cuyo término acaba en los próximos 7 días.
        // `expires_at` es la fecha real de fin (mig 316), sea el término de uno
        // o de tres meses; filtrar aquí es lo mismo que hacen las promos.
        consulta: supabase
          .from('user_subscriptions')
          .select('user_id')
          .eq('status', 'activated')
          .not('expires_at', 'is', null)
          .gte('expires_at', nowIso)
          .lte('expires_at', in7dIso),
      },
    ]);
    const [promosRaw, subsRaw] = datos;

    setAvisos({
      promosExpiring: noLeidos.includes(QUE_PROMOS)
        ? null
        : ((promosRaw ?? []) as { id: string }[]).length,
      subsExpiring: noLeidos.includes(QUE_SUBS)
        ? null
        : alumnosDistintos((subsRaw ?? []) as { user_id: string | null }[]),
    });
    setFallidos(noLeidos);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="card" data-testid="avisos-club-panel">
      <div className="flex items-center gap-2 mb-4">
        <Bell size={18} className="text-gold-primary" />
        <h3 className="font-oswald text-white text-lg uppercase tracking-wide">
          Avisos · Próximos a caducar
        </h3>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-text-secondary text-sm font-inter py-4">
          <div className="w-3 h-3 border border-gold-primary/40 border-t-gold-primary rounded-full animate-spin" />
          Cargando avisos…
        </div>
      ) : (
        <>
          {fallidos.length > 0 && (
            <div
              className="text-error text-sm font-inter border border-error/30 rounded px-3 py-2 mb-3 flex items-center justify-between gap-3"
              data-testid="avisos-club-fallidos"
            >
              <span>
                No se ha podido leer {listaEnCastellano(fallidos)}. Lo que salga sin número está sin
                comprobar, no vacío.
              </span>
              <button
                className="btn-secondary shrink-0"
                onClick={load}
                data-testid="avisos-club-reintentar"
              >
                Reintentar
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <AvisoChip
              testid="aviso-promos-caducan"
              icon={TicketPercent}
              color="#9C27B0"
              value={avisos.promosExpiring}
              label="Promos caducan (7 días)"
            />
            <AvisoChip
              testid="aviso-subs-expiran"
              icon={CalendarClock}
              color="#2196F3"
              value={avisos.subsExpiring}
              label="Alumnos con suscripción que expira (7 días)"
            />
          </div>
        </>
      )}
    </div>
  );
}

function AvisoChip({
  testid,
  icon: Icon,
  color,
  value,
  label,
}: {
  testid: string;
  icon: typeof Bell;
  color: string;
  value: Cuenta;
  label: string;
}) {
  return (
    <div
      data-testid={testid}
      className="flex items-center justify-between bg-bg-elevated border border-bg-border rounded-lg p-3"
    >
      <div className="flex items-center gap-2">
        <Icon size={16} style={{ color }} />
        <span className="text-text-secondary font-inter text-xs">{label}</span>
      </div>
      <span
        className={`font-oswald text-2xl leading-none ${value === null ? 'text-error' : 'text-white'}`}
        data-testid={`${testid}-count`}
        title={value === null ? 'Sin comprobar: la consulta ha fallado' : undefined}
      >
        {value === null ? '—' : value}
      </span>
    </div>
  );
}
